/**
 * useAuth — bootstrap Worker JWT from LIFF idToken on mount.
 * Returns status: 'loading' | 'ok' | 'unpaired' | 'error' | 'no-liff'
 *
 * Cross-app handoff: if the URL hash carries `#token=...` (sent by the
 * HumanAI launcher from another app), we adopt that JWT, fetch profile
 * via /me, and persist — avoiding a re-login.
 */
import { useEffect, useRef, useState } from 'react';
import { useCallback } from 'react';
import { getIDToken, isIdTokenExpired, isLoggedIn, isInLineClient, login as liffLogin } from '../lib/liff';
import { apiFetch, ApiError } from '../lib/api-client';
import { useAuthStore } from '../lib/store';
import type { AuthProfile } from '@payroll/shared';

/** Pull `#token=...` out of the URL once. Returns the token (or null)
 *  and rewrites the hash so the JWT doesn't linger in the address bar. */
function consumeTokenHash(): string | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash;
  if (!hash.startsWith('#token=')) return null;
  const token = decodeURIComponent(hash.slice('#token='.length));
  if (!token) return null;
  // Strip the hash without triggering a navigation.
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  return token;
}

type Status = 'loading' | 'ok' | 'unpaired' | 'error' | 'no-liff';

// One-shot guard around the liff.login() redirect we fire when the LINE
// idToken has expired. liff.getIDToken() never auto-refreshes, so a stale
// token would otherwise dead-end on the error screen. We mark the attempt in
// sessionStorage with a timestamp BEFORE redirecting; if we land back here and
// the token is STILL bad within the window (clock skew, channel misconfig),
// we stop instead of looping LINE OAuth forever. Cleared on a successful auth.
const RELOGIN_KEY = 'pp_liff_relogin_at';
const RELOGIN_WINDOW_MS = 30_000;

function recentlyAttemptedRelogin(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOGIN_KEY) ?? '0');
    return at > 0 && Date.now() - at < RELOGIN_WINDOW_MS;
  } catch {
    return false;
  }
}

/** Re-run LINE login to mint a fresh idToken. Returns true if a redirect was
 *  triggered (caller should stop and let the page reload), false if we must
 *  not loop or must not force LINE.
 *
 *  Gated to the LINE in-app client only: there the user is inherently using
 *  LINE, so refreshing the idToken is seamless (no password prompt). In an
 *  external browser we must NOT auto-redirect to LINE OAuth — the login page
 *  leads with email+password and offers LINE as just one button the user can
 *  choose. Forcing the redirect there is the "เด้ง LINE บนเบราว์เซอร์" trap. */
function refreshLineSession(): boolean {
  if (!isInLineClient() || !isLoggedIn() || recentlyAttemptedRelogin()) return false;
  try {
    sessionStorage.setItem(RELOGIN_KEY, String(Date.now()));
  } catch {
    /* ignore quota / privacy-mode failures — worst case we just don't loop-guard */
  }
  liffLogin();
  return true;
}

function clearReloginGuard(): void {
  try {
    sessionStorage.removeItem(RELOGIN_KEY);
  } catch {
    /* ignore */
  }
}

export function useAuth() {
  const { profile, jwt, setAuth, clear } = useAuthStore();
  const [status, setStatus] = useState<Status>(jwt ? 'ok' : 'loading');
  const [error, setError] = useState<string | null>(null);
  // Bootstrap runs once per mount. Without this guard, re-issuing the JWT on
  // every entry (below) would loop: setAuth → re-render → effect → re-issue.
  const bootstrapped = useRef(false);

  const refreshAuth = useCallback(async (force = false) => {
    // Cross-app handoff: another HumanAI app forwarded us with #token=…
    // → adopt it as the active session (skipping LIFF entirely).
    const handedOff = consumeTokenHash();
    if (handedOff) {
      try {
        const me = await apiFetch<AuthProfile>('/me', { token: handedOff });
        setAuth(handedOff, me);
        setStatus('ok');
        return;
      } catch {
        // Fall through to normal bootstrap; expired/forged tokens just
        // bounce the user to the regular login path.
      }
    }

    const idToken = getIDToken();
    // "No usable LINE identity" = no token at all, OR (in a browser) a lingering
    // EXPIRED token. In the browser we follow the web-login model: a persisted
    // JWT is authoritative and we never force a LINE redirect — the login page
    // leads with email+password and offers LINE as one optional button. A stale
    // idToken left in localStorage must not bounce a happy password user. The
    // seamless idToken re-issue below is reserved for the LINE in-app client,
    // where the idToken is genuinely the source of truth.
    if (!idToken || (!isInLineClient() && isIdTokenExpired(idToken))) {
      const { jwt: curJwt, profile: curProfile } = useAuthStore.getState();
      if (!force && curJwt && curProfile) {
        setStatus('ok');
        return;
      }
      setStatus('no-liff');
      return;
    }

    // LIFF context: the LINE idToken is the source of truth, so we ALWAYS
    // re-issue the worker JWT — even when one is already persisted. A persisted
    // JWT is just a warm-start cache (we keep showing its profile while this
    // runs, so no loading flash). Trusting it blindly let a re-paired account
    // keep riding a stale 7-day JWT bound to the previously-linked employee on
    // mobile — the exact "ลิงก์ผิดคน → แก้แล้วแต่มือถือยังขึ้นคนเดิม" bug.
    if (force) clear();
    const { jwt: curJwt, profile: curProfile } = useAuthStore.getState();
    if (!curJwt || !curProfile) setStatus('loading');
    setError(null);

    // Reached only inside the LINE in-app client (browser + expired token was
    // handled above as web-login). liff.getIDToken() is minted at login time and
    // NOT auto-refreshed — once expired, /auth/me fails with AUTH_FAILED
    // "invalid id token" (LINE verify → 400 "IdToken expired."). Re-run
    // liff.login() to mint a fresh token instead of dead-ending. Guarded so it
    // can't loop.
    if (isIdTokenExpired(idToken)) {
      if (refreshLineSession()) {
        setStatus('loading'); // redirect in flight; page reloads with a fresh token
        return;
      }
      clear();
      setStatus('no-liff');
      return;
    }

    try {
      const result = await apiFetch<{ jwt: string; profile: AuthProfile }>('/auth/me', {
        method: 'POST',
        body: { idToken },
        skipAuth: true,
      });
      clearReloginGuard();
      setAuth(result.jwt, result.profile);
      setStatus('ok');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'EMPLOYEE_NOT_MAPPED') {
        setStatus('unpaired');
      } else if (err instanceof ApiError && err.code === 'AUTH_FAILED') {
        // Token was syntactically fine but LINE rejected it (e.g. expired
        // between our exp check and the round-trip, or revoked). Try one
        // guarded re-login before surfacing the error.
        if (refreshLineSession()) {
          setStatus('loading');
          return;
        }
        clear();
        setStatus('no-liff');
      } else {
        // Other failures: show the error and let ErrorScreen's "ลองใหม่"
        // button drive recovery. The apiFetch layer also re-issues the worker
        // JWT on stale-token 401s for already-authenticated requests.
        clear();
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Auth failed');
      }
    }
  }, [setAuth, clear]);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    void refreshAuth(false);
  }, [refreshAuth]);

  return {
    profile,
    jwt,
    // มี jwt+profile (เช่นหลัง web-login setAuth) = ใช้งานได้ทันที → 'ok' โดยไม่ต้องรอ
    // refreshAuth rerun (bootstrapped ครั้งเดียว). unpaired/error/loading จะ clear jwt/profile
    // อยู่แล้ว → ตกไปใช้ status ภายในตามเดิม.
    status: jwt && profile ? 'ok' : status,
    error,
    logout: clear,
    refreshAuth,
  };
}
